import { MenuItem, TableCell, TableRow } from "@mui/material";
import { useState } from "react";
import { useMutation } from "react-query";
import BootstrapModal from "react-bootstrap/Modal";
import Select from "react-select";
import { toast } from "react-toastify";
import useLang from "Shared/hooks/useLanguage";
import {
  StyledDropButtonThreeDots,
  StyledDropMenuMoreAction,
} from "Utils/Style/Dropdownstyle";
import { reactSelectSMStyle, styles } from "Utils/Common";
import {
  BatchControlsSave,
  BatchControlsToggleStatus,
} from "Services/BatchControls/BatchControls";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";

const fullWidthSelectStyles = {
  ...reactSelectSMStyle,
  control: (base: any) => ({
    ...(reactSelectSMStyle.control
      ? (reactSelectSMStyle.control as (base: any) => any)(base)
      : base),
    width: "100%",
    minWidth: "100%",
  }),
};

const controlTypeOptions = [
  { value: "Positive", label: "Positive" },
  { value: "Negative", label: "Negative" },
];

const numericFields = ["controlValue", "currentLot", "previousLot"];

function ActiveControlsRow({
  row,
  index,
  setLocalRows,
  refetch,
  setIsBtnDisabled,
}: any) {
  const { t } = useLang();
  const [anchorEl, setAnchorEl] = useState<any>({ dropdown1: null });
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  const isNewRow = row.batchControlId === 0;

  const handleClick = (event: any, name: string) =>
    setAnchorEl({ ...anchorEl, [name]: event.currentTarget });

  const handleClose = (name: string) =>
    setAnchorEl({ ...anchorEl, [name]: null });

  const handleChange = (field: string, value: string) => {
    const normalizedValue = numericFields.includes(field)
      ? value.replace(/\D/g, "")
      : value;

    if (isNewRow) {
      setLocalRows((curr: any[]) =>
        curr.map((x: any, i: number) =>
          i === index ? { ...x, [field]: normalizedValue } : x
        )
      );
    } else {
      setLocalRows((curr: any[]) =>
        curr.map((x: any) =>
          x.batchControlId === row.batchControlId
            ? { ...x, [field]: normalizedValue }
            : x
        )
      );
    }
  };

  const getValues = (r: any) => {
    setLocalRows((curr: any[]) => [
      { ...r, rowStatus: true },
      ...curr.filter((x: any) => x.batchControlId !== r.batchControlId),
    ]);
  };

  const removeFromLocalRows = () => {
    if (isNewRow) {
      setLocalRows((curr: any[]) =>
        curr.filter((_: any, i: number) => i !== index)
      );
    } else {
      setLocalRows((curr: any[]) =>
        curr.filter((x: any) => x.batchControlId !== row.batchControlId)
      );
    }
  };

  const handleRowCancel = () => {
    handleClose("dropdown1");
    removeFromLocalRows();
    if (isNewRow) setIsBtnDisabled(false);
  };

  /* -------- useMutation: Save -------- */
  const saveMutation = useMutation(
    (payload: any) => BatchControlsSave(payload),
    {
      onSuccess: (res: any) => {
        if (res?.data?.statusCode === 200 || res?.data?.statusCode === 201) {
          toast.success(res?.data?.data?.message);
          setIsBtnDisabled(false);
          handleClose("dropdown1");
          removeFromLocalRows();
          refetch();
        } else {
          toast.error(res?.data?.data?.message || t("Something went wrong."));
        }
      },
      onError: () => {
        toast.error(t("An unexpected error occurred."));
      },
    }
  );

  /* -------- useMutation: Deactivate -------- */
  const deactivateMutation = useMutation(
    (id: number) => BatchControlsToggleStatus(id),
    {
      onSuccess: (res: any) => {
        if (res?.data?.statusCode === 200) {
          toast.success(res?.data?.data?.message);
          handleClose("dropdown1");
          setShowDeactivateModal(false);
          refetch();
        } else {
          toast.error(res?.data?.data?.message || t("Something went wrong."));
        }
      },
      onError: () => {
        toast.error(t("An unexpected error occurred."));
      },
    }
  );

  const handleSave = () => {
    if (!row.controlName?.trim()) {
      toast.error(t("Control name cannot be empty."));
      return;
    }

    const controlValue = String(row.controlValue ?? "").trim();
    const currentLot = String(row.currentLot ?? "").trim();
    const previousLot = String(row.previousLot ?? "").trim();
    const isNumericOnly = (val: string) => /^\d+$/.test(val);

    if (!isNumericOnly(controlValue)) {
      toast.error(t("Control value must be a number."));
      return;
    }

    if (!isNumericOnly(currentLot)) {
      toast.error(t("Current lot must be a number."));
      return;
    }

    if (!isNumericOnly(previousLot)) {
      toast.error(t("Previous lot must be a number."));
      return;
    }

    saveMutation.mutate({
      batchControlId: row.batchControlId ?? 0,
      controlName: row.controlName,
      controlType: row.controlType ?? "",
      controlValue: row.controlValue ?? "",
      currentLot: row.currentLot ?? "",
      previousLot: row.previousLot ?? "",
      isActive: true,
    });
  };

  const handleDeactivate = () => {
    if (!row.batchControlId) return toast.info(t("No Row Selected"));
    deactivateMutation.mutate(row.batchControlId);
  };

  const openDeactivateModal = () => {
    handleClose("dropdown1");
    setShowDeactivateModal(true);
  };

  return (
    <>
      <TableRow className="h-30px">
        {/* Actions */}
        <TableCell>
          {row.rowStatus ? (
            <div className="d-flex justify-content-center gap-2">
              <button
                disabled={saveMutation.isLoading}
                id={`ActiveControlSave_${row.batchControlId}`}
                className="btn btn-icon btn-sm fw-bold btn-table-save btn-icon-light h-32px w-32px fas-icon-20px"
                onClick={handleSave}
              >
                <i className="bi bi-check2" />
              </button>
              <button
                id={`ActiveControlCancel_${row.batchControlId}`}
                className="btn btn-icon btn-sm fw-bold btn-table-cancel btn-icon-light h-32px w-32px fas-icon-20px"
                onClick={handleRowCancel}
              >
                <i className="bi bi-x" />
              </button>
            </div>
          ) : (
            <div className="d-flex justify-content-center">
              <StyledDropButtonThreeDots
                id={`ActiveControl3Dots_${row.batchControlId}`}
                aria-haspopup="true"
                onClick={(e: any) => handleClick(e, "dropdown1")}
                className="btn btn-light-info btn-sm btn-icon moreactions min-w-auto rounded-4"
              >
                <i className="bi bi-three-dots-vertical p-0 icon" />
              </StyledDropButtonThreeDots>
              <StyledDropMenuMoreAction
                anchorEl={anchorEl.dropdown1}
                open={Boolean(anchorEl.dropdown1)}
                onClose={() => handleClose("dropdown1")}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                <PermissionComponent
                  moduleName="Oncology LIS"
                  pageName="Controls"
                  permissionIdentifier="Edit"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <MenuItem className="p-0">
                      <a
                        id={`ActiveControlEdit_${row.batchControlId}`}
                        className="w-150px p-0 text-dark"
                        onClick={() => {
                          getValues(row);
                          handleClose("dropdown1");
                        }}
                      >
                        <div className="menu-item px-3">
                          <i className="fa fa-edit text-primary mr-2 w-20px" />
                          {getPermissionDisplayName(
                            pageName,
                            moduleName,
                            permissionIdentifier,
                            "Edit"
                          )}
                        </div>
                      </a>
                    </MenuItem>
                  )}
                </PermissionComponent>
                <PermissionComponent
                  moduleName="Oncology LIS"
                  pageName="Controls"
                  permissionIdentifier="Inactive"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <MenuItem className="p-0">
                      <a
                        id={`ActiveControlDeactivate_${row.batchControlId}`}
                        className="w-150px p-0 text-dark"
                        onClick={openDeactivateModal}
                      >
                        <div className="menu-item px-3">
                          <i className="fa fa-ban text-danger mr-2 w-20px" />
                          {getPermissionDisplayName(
                            pageName,
                            moduleName,
                            permissionIdentifier,
                            "Inactive"
                          )}
                        </div>
                      </a>
                    </MenuItem>
                  )}
                </PermissionComponent>
              </StyledDropMenuMoreAction>
            </div>
          )}
        </TableCell>

        {/* Controls Name */}
        <TableCell sx={{ width: "max-content" }}>
          {row.rowStatus ? (
            <input
              type="text"
              name="controlName"
              placeholder={t("Controls Name")}
              className="form-control bg-white mb-lg-0 min-w-150px w-100 rounded h-30px"
              value={row.controlName ?? ""}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
          ) : (
            <div style={{ width: "max-content" }}>{row.controlName}</div>
          )}
        </TableCell>

        {/* Controls Value */}
        <TableCell sx={{ width: "max-content" }}>
          {row.rowStatus ? (
            <input
              type="text"
              name="controlValue"
              placeholder={t("Controls Value")}
              className="form-control bg-white mb-lg-0 min-w-100px w-100 rounded h-30px"
              value={row.controlValue ?? ""}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
          ) : (
            <div style={{ width: "max-content" }}>{row.controlValue}</div>
          )}
        </TableCell>

        {/* Current Lot # */}
        <TableCell sx={{ width: "max-content" }}>
          {row.rowStatus ? (
            <input
              type="text"
              name="currentLot"
              placeholder={t("Current Lot #")}
              className="form-control bg-white mb-lg-0 min-w-100px w-100 rounded h-30px"
              value={row.currentLot ?? ""}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
          ) : (
            <div style={{ width: "max-content" }}>{row.currentLot}</div>
          )}
        </TableCell>

        {/* Previous Lot # */}
        <TableCell sx={{ width: "max-content" }}>
          {row.rowStatus ? (
            <input
              type="text"
              name="previousLot"
              placeholder={t("Previous Lot #")}
              className="form-control bg-white mb-lg-0 min-w-100px w-100 rounded h-30px"
              value={row.previousLot ?? ""}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
          ) : (
            <div style={{ width: "max-content" }}>{row.previousLot}</div>
          )}
        </TableCell>

        {/* Controls Type */}
        <TableCell sx={{ width: "max-content" }}>
          {row.rowStatus ? (
            <Select
              menuPortalTarget={document.body}
              styles={fullWidthSelectStyles}
              theme={(theme: any) => styles(theme)}
              placeholder={`--- ${t("Select")} ---`}
              options={controlTypeOptions}
              className="w-100"
              value={
                controlTypeOptions.find((o) => o.value === row.controlType) ??
                null
              }
              onChange={(option: any) =>
                handleChange("controlType", option?.value ?? "")
              }
            />
          ) : (
            <div style={{ width: "max-content" }}>{row.controlType}</div>
          )}
        </TableCell>
      </TableRow>

      {/* Deactivate Confirmation Modal */}
      <BootstrapModal
        show={showDeactivateModal}
        onHide={() => setShowDeactivateModal(false)}
        backdrop="static"
        keyboard={false}
      >
        <BootstrapModal.Header closeButton className="bg-light-primary m-0 p-5">
          <h4>{t("Inactive Control")}</h4>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          {t("Are you sure you want to deactivate this control?")}
        </BootstrapModal.Body>
        <BootstrapModal.Footer className="p-0">
          <button
            id={`ActiveControlModalDeactivateCancel_${row.batchControlId}`}
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowDeactivateModal(false)}
          >
            {t("Cancel")}
          </button>
          <button
            id={`ActiveControlModalDeactivateConfirm_${row.batchControlId}`}
            type="button"
            className="btn btn-danger m-2"
            onClick={handleDeactivate}
          >
            {t("Inactive")}
          </button>
        </BootstrapModal.Footer>
      </BootstrapModal>
    </>
  );
}

export default ActiveControlsRow;
